import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaTimes, FaMoneyBillWave, FaUniversity, FaCreditCard, FaFileInvoice } from 'react-icons/fa';
import PlantillaModal from '../util/PlantillaModal';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import { roundTo2 } from '../../miscelaneo/monetario';

const FORMAS_PAGO = [
  { value: '01', label: 'Efectivo', icon: FaMoneyBillWave },
  { value: '02', label: 'Transferencia / Depósito', icon: FaUniversity },
  { value: '03', label: 'Tarjeta Crédito/Débito', icon: FaCreditCard },
  { value: '04', label: 'Cheque', icon: FaFileInvoice }
];


export default function ModalPagoPOS({ 
  mostrar, 
  onClose, 
  ticket, 
  onConfirmarPago, 
  formatoMoneda,
  modo = 'pos',
  facturaExistente = null
}) {
  const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState('contado');
  const [metodosPago, setMetodosPago] = useState([{
    metodoPago: '01',
    monto: '',
    detalles: {}
  }]);
  const [mostrarAbono, setMostrarAbono] = useState(false);
  const [cuentasTesoreria, setCuentasTesoreria] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [pasarelas, setPasarelas] = useState([]);
  const [procesando, setProcesando] = useState(false);

  // Sesión de caja activa (para pre-seleccionar cuenta en efectivo CxC)
  const [sesionCajaActiva, setSesionCajaActiva] = useState(null);
  // Pasarela de pago por defecto (desde configuración del sistema)
  const [pasarelaDefecto, setPasarelaDefecto] = useState(null);

  // Cargar cuentas de tesorería, bancos, pasarelas, configuración y sesión de caja activa
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const promesas = [
          instanciaAxios.get('/cuenta_tesoreria/resumida'),
          instanciaAxios.get('/banco'),
          instanciaAxios.get('/pasarela-pago'),
          instanciaAxios.get('/miscelaneo/configuracion').catch(() => ({ data: {} }))
        ];
        // En modo CxC o CxP, también obtener sesión de caja activa
        if (modo === 'cxc' || modo === 'cxp') {
          promesas.push(instanciaAxios.get('/sesion-caja/activa'));
        }
        const respuestas = await Promise.all(promesas);
        setCuentasTesoreria(respuestas[0].data || []);
        setBancos(respuestas[1].data || []);
        setPasarelas(respuestas[2].data || []);
        // Configuración del sistema — pasarela por defecto
        const configData = respuestas[3].data;
        if (configData?.id_pasarela_pago_defecto) {
          setPasarelaDefecto(configData.id_pasarela_pago_defecto);
        }
        if ((modo === 'cxc' || modo === 'cxp') && respuestas[4]) {
          const sesion = respuestas[4].data;
          if (sesion.tiene_sesion && sesion.sesion_activa) {
            setSesionCajaActiva(sesion.sesion_activa);
          }
        }
      } catch (error) {
        console.error('Error al cargar datos:', error);
      }
    };
    if (mostrar) {
      cargarDatos();
    }
  }, [mostrar, modo]);

  // Resetear estados cuando se abre el modal
  useEffect(() => {
    if (mostrar && ticket) {
      setTipoPagoSeleccionado('contado');
      // Pre-seleccionar cuenta de caja activa para efectivo en modo CxC/CxP
      const detallesIniciales = {};
      if ((modo === 'cxc' || modo === 'cxp') && sesionCajaActiva?.id_cuenta_tesoreria) {
        detallesIniciales.cuentaDestino = sesionCajaActiva.id_cuenta_tesoreria.toString();
      }
      setMetodosPago([{
        metodoPago: '01',
        monto: roundTo2(ticket.totales?.total || 0).toFixed(2),
        detalles: detallesIniciales
      }]);
      setMostrarAbono(false);
    }
  }, [mostrar, ticket, modo, sesionCajaActiva]);

  const actualizarMetodoPago = (index, campo, valor) => {
    const nuevosMetodos = [...metodosPago];
    if (campo === 'metodoPago') {
      nuevosMetodos[index].metodoPago = valor;
      // Limpiar detalles al cambiar método, pre-cargar defaults
      const detallesNuevos = {};
      if (valor === '01' && sesionCajaActiva?.id_cuenta_tesoreria) {
        // Efectivo: pre-seleccionar cuenta de caja activa
        detallesNuevos.cuentaDestino = sesionCajaActiva.id_cuenta_tesoreria.toString();
      } else if (valor === '02') {
        // Default fecha/hora a "ahora" para transferencia
        detallesNuevos.fechaHora = new Date().toISOString().slice(0, 16);
      } else if (valor === '03' && pasarelaDefecto) {
        detallesNuevos.id_pasarela_pago = pasarelaDefecto;
        // Auto-resolver cuenta tesorería de la pasarela por defecto
        const pasarelaObj = pasarelas.find(p => p.id_pasarela_pago === pasarelaDefecto);
        if (pasarelaObj?.id_cuenta_tesoreria) {
          detallesNuevos.cuentaDestino = pasarelaObj.id_cuenta_tesoreria.toString();
        }
      }
      nuevosMetodos[index].detalles = detallesNuevos;
    } else if (campo === 'monto') {
      nuevosMetodos[index].monto = valor;
      
      // Si hay 2 métodos y se está editando el primero, actualizar el segundo automáticamente
      if (nuevosMetodos.length === 2 && index === 0) {
        const montoMetodo1 = parseFloat(valor) || 0;
        const saldoPendiente = roundTo2(ticket.totales.total - montoMetodo1);
        if (saldoPendiente >= 0) {
          nuevosMetodos[1].monto = saldoPendiente.toFixed(2);
        }
      }
    } else {
      // Actualizar detalles
      const detallesActualizados = {
        ...nuevosMetodos[index].detalles,
        [campo]: valor
      };
      // Auto-resolver cuenta tesorería cuando cambia la pasarela
      if (campo === 'id_pasarela_pago' && valor) {
        const pasarelaObj = pasarelas.find(p => p.id_pasarela_pago === parseInt(valor));
        if (pasarelaObj?.id_cuenta_tesoreria) {
          detallesActualizados.cuentaDestino = pasarelaObj.id_cuenta_tesoreria.toString();
        }
      }
      nuevosMetodos[index].detalles = detallesActualizados;
    }
    setMetodosPago(nuevosMetodos);
  };

  const agregarMetodoPago = () => {
    if (metodosPago.length < 2) {
      // Calcular cuánto falta por pagar
      const totalPagado = metodosPago.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
      const saldoPendiente = ticket.totales.total - totalPagado;
      
      setMetodosPago([...metodosPago, {
        metodoPago: '01',
        monto: saldoPendiente > 0 ? roundTo2(saldoPendiente).toFixed(2) : '',
        detalles: {}
      }]);
    }
  };

  const eliminarMetodoPago = (index) => {
    const nuevosMetodos = metodosPago.filter((_, i) => i !== index);
    setMetodosPago(nuevosMetodos);
  };

  const validarPago = () => {
    // Modo CxC / CxP: validación para pagos contra facturas/compras existentes
    if (modo === 'cxc' || modo === 'cxp') {
      const pago = metodosPago[0];
      const monto = parseFloat(pago?.monto) || 0;

      if (monto <= 0) {
        toast.error('El monto del pago debe ser mayor a 0');
        return false;
      }

      // Validar que no exceda el balance pendiente
      const balancePendiente = roundTo2(ticket.totales.total);
      if (roundTo2(monto) > balancePendiente) {
        toast.error(`El monto no puede exceder el balance pendiente (${formatoMoneda(balancePendiente)})`);
        return false;
      }

      // Validar detalles según forma de pago
      if (pago.metodoPago === '01') { // Efectivo
        if (!pago.detalles.cuentaDestino) {
          toast.error('Seleccione la cuenta de tesorería para el pago en efectivo');
          return false;
        }
      } else if (pago.metodoPago === '02') { // Transferencia
        if (!pago.detalles.cuentaDestino) {
          toast.error('Seleccione la cuenta destino para la transferencia');
          return false;
        }
      } else if (pago.metodoPago === '04') { // Cheque
        if (!pago.detalles.bancoEmisor) {
          toast.error('Seleccione el banco emisor del cheque');
          return false;
        }
        if (!pago.detalles.numeroCheque) {
          toast.error('Ingrese el número de cheque');
          return false;
        }
        if (!pago.detalles.fechaCobro) {
          toast.error('Seleccione la fecha de cobro del cheque');
          return false;
        }
        if (!pago.detalles.cuentaDestino) {
          toast.error('Seleccione la cuenta destino donde se depositará el cheque');
          return false;
        }
      }
      // Card ('03'): no extra validation — treasury auto-resolves from pasarela

      return true;
    }

    // Modo POS
    if (tipoPagoSeleccionado === 'contado') {
      // Validar que todos los métodos tengan monto
      if (metodosPago.some(p => !p.monto || parseFloat(p.monto) <= 0)) {
        toast.error('Todos los métodos de pago deben tener un monto válido');
        return false;
      }

      const totalPagos = metodosPago.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
      if (totalPagos < ticket.totales.total) {
        toast.error('La suma de los pagos debe ser igual o mayor al total de la factura');
        return false;
      }

      // Validar detalles según forma de pago
      for (const pago of metodosPago) {
        if (pago.metodoPago === '02') { // Transferencia
          if (!pago.detalles.bancoOrigen ||
              !pago.detalles.cuentaDestino || !pago.detalles.fechaHora) {
            toast.error('Complete los campos requeridos para la transferencia');
            return false;
          }
        } else if (pago.metodoPago === '04') { // Cheque
          if (!pago.detalles.bancoEmisor || !pago.detalles.numeroCheque || !pago.detalles.fechaCobro) {
            toast.error('Complete los campos requeridos para el cheque');
            return false;
          }
        }
      }
    } else if (tipoPagoSeleccionado === 'credito' && mostrarAbono) {
      const abono = parseFloat(metodosPago[0]?.monto) || 0;
      if (abono <= 0) {
        toast.error('El abono debe ser mayor a 0');
        return false;
      }
      if (abono >= ticket.totales.total) {
        toast.error('El abono no puede ser igual o mayor al total. Use "A Contado" en su lugar');
        return false;
      }
    }

    return true;
  };

  // Atajo de teclado: Enter para confirmar pago
  useEffect(() => {
    if (!mostrar) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !procesando) {
        e.preventDefault();
        handleConfirmar();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mostrar, procesando, metodosPago, tipoPagoSeleccionado, mostrarAbono]);

  const handleConfirmar = async () => {
    if (!validarPago()) return;

    const datosPago = {
      tipoPago: tipoPagoSeleccionado,
      metodosPago: metodosPago.map(m => ({
        metodoPago: m.metodoPago,
        monto: roundTo2(parseFloat(m.monto) || 0),
        detalles: m.detalles
      })),
      abonoInicial: (tipoPagoSeleccionado === 'credito' && mostrarAbono) 
        ? roundTo2(parseFloat(metodosPago[0]?.monto) || 0) 
        : null
    };

    // For CxC mode, save payment directly
    if (modo === 'cxc' && facturaExistente) {
      try {
        setProcesando(true);
        const primerPago = datosPago.metodosPago[0];
        
        await instanciaAxios.post(`/pago-factura/factura/${facturaExistente.id_factura}`, {
          monto: roundTo2(primerPago.monto),
          id_metodo_pago: primerPago.metodoPago === '01' ? 1 : 
                          primerPago.metodoPago === '02' ? 2 :
                          primerPago.metodoPago === '03' ? 3 : 4,
          id_cuenta_tesoreria: primerPago.detalles?.cuentaDestino ? parseInt(primerPago.detalles.cuentaDestino) : null,
          id_moneda: 2,
          referencia: primerPago.detalles?.referencia || primerPago.detalles?.autorizacion || null,
          id_pasarela_pago: primerPago.detalles?.id_pasarela_pago ? parseInt(primerPago.detalles.id_pasarela_pago) : null,
          // Payment detail columns
          ultimos_4_digitos: primerPago.detalles?.ultimos4Digitos || null,
          id_banco_origen: primerPago.detalles?.bancoOrigen ? parseInt(primerPago.detalles.bancoOrigen) : null,
          numero_cheque: primerPago.detalles?.numeroCheque || null,
          nombre_titular_cheque: primerPago.detalles?.titular || null,
          fecha_cobro_cheque: primerPago.detalles?.fechaCobro || null
        });
        
        toast.success('Pago registrado exitosamente');
        onConfirmarPago(datosPago);
        onClose();
      } catch (error) {
        console.error('Error al registrar pago:', error);
        toast.error(error.response?.data?.mensaje || 'Error al registrar pago');
      } finally {
        setProcesando(false);
      }
    } else if (modo === 'cxp' && facturaExistente) {
      // For CxP mode, save purchase payment directly
      try {
        setProcesando(true);
        const primerPago = datosPago.metodosPago[0];

        await instanciaAxios.post('/pago/compra', {
          monto_total: roundTo2(primerPago.monto),
          fecha_hora: new Date().toISOString(),
          es_exterior: false,
          id_metodo_pago: primerPago.metodoPago === '01' ? 1 :
                          primerPago.metodoPago === '02' ? 2 :
                          primerPago.metodoPago === '03' ? 3 : 4,
          id_moneda: 2,
          id_cuenta_tesoreria: primerPago.detalles?.cuentaDestino ? parseInt(primerPago.detalles.cuentaDestino) : null,
          id_entidad_proveedor: facturaExistente.id_entidad_proveedor,
          retencion_total: 0,
          id_pasarela_pago: primerPago.detalles?.id_pasarela_pago ? parseInt(primerPago.detalles.id_pasarela_pago) : null,
          comentario: '',
          detallePago: [{
            id_factura: facturaExistente.id_compra,
            monto: roundTo2(primerPago.monto)
          }]
        });

        toast.success('Pago de compra registrado exitosamente');
        onConfirmarPago(datosPago);
        onClose();
      } catch (error) {
        console.error('Error al registrar pago de compra:', error);
        toast.error(error.response?.data?.mensaje || 'Error al registrar pago de compra');
      } finally {
        setProcesando(false);
      }
    } else {
      // For POS mode, return payment data to parent
      onConfirmarPago(datosPago);
    }
  };

  const calcularTotalPagos = () => {
    return roundTo2(metodosPago.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0));
  };

  const calcularCambio = () => {
    const total = calcularTotalPagos();
    return total > ticket.totales.total ? roundTo2(total - ticket.totales.total) : 0;
  };

  if (!ticket) return null;

  return (
    <PlantillaModal
      mostrar={mostrar}
      onClose={onClose}
      titulo="Procesar Pago"
    >
      <div className="space-y-3">
        {/* Resumen del total - compacto */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <div className="flex justify-between items-center text-sm mb-1">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-semibold">{formatoMoneda(ticket.totales.subtotal)}</span>
          </div>
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-gray-600">ITBIS:</span>
            <span className="font-semibold">{formatoMoneda(ticket.totales.itbis)}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-300">
            <span className="text-base font-bold">Total a pagar:</span>
            <span className="text-xl font-bold text-color-primario">
              {formatoMoneda(ticket.totales.total)}
            </span>
          </div>
        </div>

        {/* Selector: Contado vs Crédito - solo en modo POS */}
        {modo === 'pos' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Pago <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <label className="flex-1 cursor-pointer">
                <div className={`p-2 rounded-lg border transition-all ${
                  tipoPagoSeleccionado === 'contado'
                    ? 'border-color-primario bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                }`}>
                  <input
                    type="radio"
                    name="tipoPago"
                    value="contado"
                    checked={tipoPagoSeleccionado === 'contado'}
                    onChange={(e) => {
                      setTipoPagoSeleccionado(e.target.value);
                      setMostrarAbono(false);
                      setMetodosPago([{
                        metodoPago: '01',
                        monto: roundTo2(ticket.totales.total).toFixed(2),
                        detalles: {}
                      }]);
                    }}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-sm font-semibold">A Contado</div>
                  </div>
                </div>
              </label>

              <label className="flex-1 cursor-pointer">
                <div className={`p-2 rounded-lg border transition-all ${
                  tipoPagoSeleccionado === 'credito'
                    ? 'border-color-primario bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                }`}>
                  <input
                    type="radio"
                    name="tipoPago"
                    value="credito"
                    checked={tipoPagoSeleccionado === 'credito'}
                    onChange={(e) => {
                      setTipoPagoSeleccionado(e.target.value);
                      setMostrarAbono(false);
                      setMetodosPago([{ metodoPago: '01', monto: '', detalles: {} }]);
                    }}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-sm font-semibold">A Crédito</div>
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Contenido según tipo de pago - en modo CxC/CxP siempre mostrar métodos de pago */}
        {(tipoPagoSeleccionado === 'contado' || modo === 'cxc' || modo === 'cxp') && (
          <div className="space-y-4">
            {/* Métodos de pago */}
            {metodosPago.map((pago, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    {metodosPago.length > 1 ? `Método de Pago ${index + 1}` : 'Forma de Pago'}
                    <span className="text-red-500">*</span>
                  </label>
                  {index > 0 && (
                    <button
                      onClick={() => eliminarMetodoPago(index)}
                      className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                    >
                      <FaTimes /> Eliminar
                    </button>
                  )}
                </div>

                {/* Selector de forma de pago */}
                <div className="flex gap-2">
                  {FORMAS_PAGO.map((forma) => {
                    const IconComponent = forma.icon;
                    return (
                      <button
                        key={forma.value}
                        type="button"
                        onClick={() => actualizarMetodoPago(index, 'metodoPago', forma.value)}
                        className={`flex-1 p-2 rounded-lg border transition-all flex flex-col items-center gap-1 ${
                          pago.metodoPago === forma.value
                            ? 'border-color-secundario bg-green-50 shadow-sm'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        title={forma.label}
                      >
                        <IconComponent size={16} className={pago.metodoPago === forma.value ? 'text-color-secundario' : 'text-gray-500'} />
                        <span className="text-xs font-medium text-center leading-tight">{forma.label.split(' ')[0]}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Formularios específicos según forma de pago */}
                {pago.metodoPago === '01' && (
                  <>
                    <FormularioEfectivo
                      pago={pago}
                      index={index}
                      actualizarMetodoPago={actualizarMetodoPago}
                      totalFactura={ticket.totales.total}
                      formatoMoneda={formatoMoneda}
                    />
                    {(modo === 'cxc' || modo === 'cxp') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cuenta de Tesorería <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={pago.detalles.cuentaDestino || ''}
                          onChange={(e) => actualizarMetodoPago(index, 'cuentaDestino', e.target.value)}
                          className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                          <option value="">Seleccionar cuenta</option>
                          {cuentasTesoreria
                            .filter(cuenta => cuenta.id_tipo_cuenta_tesoreria === 1)
                            .map(cuenta => (
                              <option key={cuenta.id_cuenta_tesoreria} value={cuenta.id_cuenta_tesoreria}>
                                {cuenta.descripcion}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </>
                )}

                {pago.metodoPago === '02' && (
                  <FormularioTransferencia
                    pago={pago}
                    index={index}
                    actualizarMetodoPago={actualizarMetodoPago}
                    cuentasTesoreria={cuentasTesoreria}
                    bancos={bancos}
                  />
                )}

                {pago.metodoPago === '03' && (
                  <FormularioTarjeta
                    pago={pago}
                    index={index}
                    actualizarMetodoPago={actualizarMetodoPago}
                    pasarelas={pasarelas}
                  />
                )}

                {pago.metodoPago === '04' && (
                  <FormularioCheque
                    pago={pago}
                    index={index}
                    actualizarMetodoPago={actualizarMetodoPago}
                    bancos={bancos}
                    cuentasTesoreria={cuentasTesoreria}
                    mostrarCuentaDestino={modo === 'cxc' || modo === 'cxp'}
                  />
                )}

                {/* Botón agregar otro método - debajo del formulario */}
                {index === 0 && metodosPago.length < 2 && (
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={agregarMetodoPago}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-500 hover:border-color-primario hover:text-color-primario hover:bg-gray-50 transition-all flex items-center gap-1.5 text-xs"
                      title="Agregar otro método de pago"
                    >
                      <FaPlus size={10} />
                      <span>Otro método</span>
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Resumen de pagos - solo si hay múltiples métodos */}
            {metodosPago.length > 1 && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Total de pagos:</span>
                  <span className="text-xl font-bold text-color-primario">
                    {formatoMoneda(calcularTotalPagos())}
                  </span>
                </div>
                {/* Cambio a devolver solo si hay múltiples métodos */}
                {calcularCambio() > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700 font-semibold">Cambio a devolver:</span>
                      <span className="text-2xl font-bold text-green-600">
                        {formatoMoneda(calcularCambio())}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* A Crédito */}
        {tipoPagoSeleccionado === 'credito' && (
          <div className="space-y-4">
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                Esta factura quedará registrada como cuenta por cobrar.
              </p>
            </div>

            {!mostrarAbono ? (
              <button
                onClick={() => {
                  setMostrarAbono(true);
                  setMetodosPago([{ metodoPago: '01', monto: '', detalles: {} }]);
                }}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-700 hover:border-color-primario hover:text-color-primario transition-all flex items-center justify-center gap-2"
              >
                <FaPlus size={14} />
                <span className="font-medium">Abono</span>
              </button>
            ) : (
              <div className="border-2 border-color-secundario rounded-xl p-4 bg-green-50 space-y-3">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    Abono Inicial (Efectivo)
                  </label>
                  <button
                    onClick={() => {
                      setMostrarAbono(false);
                      setMetodosPago([{ metodoPago: '01', monto: '', detalles: {} }]);
                    }}
                    className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                  >
                    <FaTimes /> Cancelar
                  </button>
                </div>

                <input
                  type="number"
                  value={metodosPago[0]?.monto || ''}
                  onChange={(e) => actualizarMetodoPago(0, 'monto', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="w-full p-3 rounded-xl text-xl text-center font-bold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />

                {metodosPago[0]?.monto && parseFloat(metodosPago[0].monto) > 0 && (
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Saldo pendiente:</span>
                      <span className="text-lg font-bold text-amber-600">
                        {formatoMoneda(roundTo2(ticket.totales.total - (parseFloat(metodosPago[0].monto) || 0)))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Botón confirmar */}
        <button
          onClick={handleConfirmar}
          disabled={procesando}
          className={`w-full py-3 rounded-lg font-bold text-base transition-all ${
            procesando 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-color-secundario text-white hover:bg-opacity-90 shadow-lg'
          }`}
        >
          {procesando ? 'Procesando...' : 'Confirmar Pago'}
        </button>
      </div>
    </PlantillaModal>
  );
}

// Componente para formulario de efectivo
function FormularioEfectivo({ pago, index, actualizarMetodoPago, totalFactura, formatoMoneda }) {
  const monto = parseFloat(pago.monto) || 0;
  const cambio = monto > totalFactura ? roundTo2(monto - totalFactura) : 0;

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Monto Recibido <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          value={pago.monto}
          onChange={(e) => actualizarMetodoPago(index, 'monto', e.target.value)}
          onWheel={(e) => e.target.blur()}
          className="w-full p-3 rounded-xl text-xl text-center font-bold border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          placeholder="0.00"
          step="0.01"
          min="0"
          autoFocus
        />
      </div>

      {monto > 0 && cambio > 0 && (
        <div className="bg-green-50 p-3 rounded-lg border-2 border-green-300">
          <div className="flex justify-between items-center">
            <span className="text-green-700 font-semibold">Cambio:</span>
            <span className="text-2xl font-bold text-green-600">
              {formatoMoneda(cambio)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Componente para formulario de transferencia
function FormularioTransferencia({ pago, index, actualizarMetodoPago, cuentasTesoreria, bancos }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monto <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={pago.monto}
            onChange={(e) => actualizarMetodoPago(index, 'monto', e.target.value)}
            onWheel={(e) => e.target.blur()}
            className="w-full p-2 rounded-xl text-center font-semibold border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Referencia
          </label>
          <input
            type="text"
            value={pago.detalles.referencia || ''}
            onChange={(e) => actualizarMetodoPago(index, 'referencia', e.target.value)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            placeholder="Núm. referencia"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Banco Origen <span className="text-red-500">*</span>
          </label>
          <select
            value={pago.detalles.bancoOrigen || ''}
            onChange={(e) => actualizarMetodoPago(index, 'bancoOrigen', e.target.value)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          >
            <option value="">Seleccionar banco</option>
            {bancos.map(banco => (
              <option key={banco.id_banco} value={banco.id_banco}>
                {banco.descripcion}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cuenta Destino <span className="text-red-500">*</span>
          </label>
          <select
            value={pago.detalles.cuentaDestino || ''}
            onChange={(e) => actualizarMetodoPago(index, 'cuentaDestino', e.target.value)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          >
            <option value="">Seleccionar</option>
            {cuentasTesoreria
              .filter(cuenta => cuenta.id_tipo_cuenta_tesoreria === 2)
              .map(cuenta => (
                <option key={cuenta.id_cuenta_tesoreria} value={cuenta.id_cuenta_tesoreria}>
                  {cuenta.descripcion}
                </option>
              ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Fecha/Hora <span className="text-red-500">*</span>
        </label>
        <input
          type="datetime-local"
          value={pago.detalles.fechaHora || ''}
          onChange={(e) => actualizarMetodoPago(index, 'fechaHora', e.target.value)}
          className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
        />
      </div>
    </div>
  );
}

// Componente para formulario de tarjeta
function FormularioTarjeta({ pago, index, actualizarMetodoPago, pasarelas }) {
  return (
    <div className="space-y-3">
      {/* Nota informativa */}
      <div className="bg-gray-50 p-2 rounded-lg border border-gray-200">
        <p className="text-xs text-gray-600">
          Los datos se obtienen del recibo generado por el terminal de pago
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monto <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={pago.monto}
            onChange={(e) => actualizarMetodoPago(index, 'monto', e.target.value)}
            onWheel={(e) => e.target.blur()}
            className="w-full p-2 rounded-xl text-center font-semibold border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Últimos 4 dígitos
          </label>
          <input
            type="text"
            value={pago.detalles.ultimos4Digitos || ''}
            onChange={(e) => {
              const valor = e.target.value.replace(/\D/g, '').slice(0, 4);
              actualizarMetodoPago(index, 'ultimos4Digitos', valor);
            }}
            className="w-full p-2 rounded-xl text-center border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            placeholder="****"
            maxLength="4"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Núm. Autorización
          </label>
          <input
            type="text"
            value={pago.detalles.autorizacion || ''}
            onChange={(e) => actualizarMetodoPago(index, 'autorizacion', e.target.value)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            placeholder="Número de autorización"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pasarela de Pago
          </label>
          <select
            value={pago.detalles.id_pasarela_pago || ''}
            onChange={(e) => actualizarMetodoPago(index, 'id_pasarela_pago', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">Sin pasarela</option>
            {pasarelas.map(pasarela => (
              <option key={pasarela.id_pasarela_pago} value={pasarela.id_pasarela_pago}>
                {pasarela.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// Componente para formulario de cheque
function FormularioCheque({ pago, index, actualizarMetodoPago, bancos, cuentasTesoreria = [], mostrarCuentaDestino = false }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Banco Emisor <span className="text-red-500">*</span>
          </label>
          <select
            value={pago.detalles.bancoEmisor || ''}
            onChange={(e) => actualizarMetodoPago(index, 'bancoEmisor', e.target.value)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          >
            <option value="">Seleccionar banco</option>
            {bancos.map(banco => (
              <option key={banco.id_banco} value={banco.id_banco}>
                {banco.descripcion}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Núm. Cheque <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={pago.detalles.numeroCheque || ''}
            onChange={(e) => actualizarMetodoPago(index, 'numeroCheque', e.target.value)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
            placeholder="Número"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monto <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={pago.monto}
            onChange={(e) => actualizarMetodoPago(index, 'monto', e.target.value)}
            onWheel={(e) => e.target.blur()}
            className="w-full p-2 rounded-xl text-center font-semibold border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha Cobro <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={pago.detalles.fechaCobro || ''}
            onChange={(e) => actualizarMetodoPago(index, 'fechaCobro', e.target.value)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          />
        </div>
      </div>

      {mostrarCuentaDestino && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cuenta Destino (Banco) <span className="text-red-500">*</span>
          </label>
          <select
            value={pago.detalles.cuentaDestino || ''}
            onChange={(e) => actualizarMetodoPago(index, 'cuentaDestino', e.target.value)}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          >
            <option value="">Seleccionar cuenta</option>
            {cuentasTesoreria
              .filter(cuenta => cuenta.id_tipo_cuenta_tesoreria === 2)
              .map(cuenta => (
                <option key={cuenta.id_cuenta_tesoreria} value={cuenta.id_cuenta_tesoreria}>
                  {cuenta.descripcion}
                </option>
              ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Titular del Cheque
        </label>
        <input
          type="text"
          value={pago.detalles.titular || ''}
          onChange={(e) => actualizarMetodoPago(index, 'titular', e.target.value)}
          className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          placeholder="Nombre del titular (opcional)"
        />
      </div>
    </div>
  );
}
